<template>
  <div>
    <Navbar />
    <Announcements />
    <section id="decks-index" @click="highlightDeck = 0" class="deck-index section">
      <b-loading :is-full-page="false" v-model="changingDeck" />
      <BlankSlateBox v-if="slideDecksLoaded && !hasAnyDecks" @new-deck="templateSelector" @upload="uploadDeck" />
      <QuickStartBanner v-else-if="slideDecksLoaded" @new-deck="templateSelector" @upload="uploadDeck" />
      <OnboardingDemo :loading="loading" :slideDecks="slideDecks" :user="user" :hasAnyDecks="hasAnyDecks" />

      <!-- skeleton loading animation -->
      <div v-if="!slideDecksLoaded" class="container deck-container has-text-centered">
        <div class="m-b-10">
          <b-skeleton height="200px" />
        </div>
        <div class="m-t-20 m-b-10">
          <b-skeleton height="3rem" />
        </div>
        <div class="deck-box m-t-40">
          <div v-for="num in 5" :key="num" class="deck-card">
            <div class="deck-thumb">
              <b-skeleton height="230px" />
            </div>
          </div>
        </div>
      </div>

      <transition name="fade">
        <div v-if="slideDecksLoaded" v-show="hasAnyDecks" :class="{'has-decks': hasAnyDecks}" class="container deck-container" data-cy="deck-container">
          <!-- DECK FILTER MENU -->
          <div v-if="deckCount > 0 || teamDecks?.length > 0 || deckView === 'team'" class="level deck-filter">
            <div class="level-left text-menu">
              <p @click="$router.push('/decks/')" :class="{'is-active': deckView === 'user'}">My decks</p>
              <span class="divider"> | </span>
              <p @click="$router.push('/decks/team')" :class="{'is-active': deckView === 'team'}">Team decks</p>
            </div>

            <div class="level-right">
              <b-input v-model="search" type="search" icon-pack="fas" icon="search" placeholder="Search my decks..." class="filter-search" />
              <b-dropdown v-model="sortOrder" type="is-primary">
                <template #trigger="{ active }">
                  <b-button
                    :icon-right="active ? 'caret-up' : 'caret-down'"
                    :label="sortOrder"
                    icon-pack="fas"
                    type="is-gray"
                  />
                </template>
                <b-dropdown-item v-for="(val, key) in sortOrderOptions" :key="'sort-' + key" :value="key">{{ key }}</b-dropdown-item>
              </b-dropdown>
              <a @click.prevent="templateSelector" class="is-primary button" style="margin-left: 8px;">
                New Slide Deck
              </a>
            </div>
          </div>

          <template v-if="deckView === 'user'">
            <DeckList
              :decks="filteredDecks.slice(0, viewLimit)"
              :highlightDeck="highlightDeck"
              :showNewDeck="true"
              :isOwnerView="true"
              class="no-margin decks-page"
            />
            <div v-if="viewLimit < filteredDecks.length" class="buttons m-t-40">
              <p @click="viewLimit += 12" class="button is-steel">Load more slide decks</p>
            </div>
            <p v-if="(deckCount > 0 || teamDecks?.length > 0) && filteredDecks.length < 1" class="no-decks user">
              <template v-if="search.length > 0">
                No results for this search
              </template>
              <template v-else>
                <BlankSlateBox @new-deck="templateSelector" @upload="uploadDeck" />
              </template>
            </p>
          </template>
          <template v-else-if="deckView === 'team' && !user.team.id">
            <TeamContactCta />
          </template>
          <template v-else>
            <h3 class="title is-5 folder">Shared by you</h3>
            <DeckList
              :decks="decksSharedByUser.slice(0, sharedByViewLimit)"
              :highlightDeck="highlightDeck"
              :showNewDeck="true"
              :isOwnerView="true"
              :deckView="deckView"
              class="no-margin decks-page"
            />
            <div v-if="sharedByViewLimit < decksSharedByUser.length" class="buttons m-t-40">
              <p @click="sharedByViewLimit += 6" class="button is-steel">Load more slide decks</p>
            </div>
            <p v-if="decksSharedByUser.length < 1" class="no-decks">
              {{ search.length > 0 ? 'No results for this search' : `You haven't shared any decks yet` }}
            </p>
            <br>

            <h3 class="title is-5 folder m-t-40">Shared with you</h3>
            <DeckList
              :decks="decksSharedWithUser.slice(0, sharedWithViewLimit)"
              :highlightDeck="highlightDeck"
              :showNewDeck="true"
              :isOwnerView="true"
              :deckView="deckView"
              class="no-margin decks-page"
            />
            <div v-if="sharedWithViewLimit < decksSharedWithUser.length" class="buttons m-t-40">
              <p @click="sharedWithViewLimit += 6" class="button is-steel">Load more slide decks</p>
            </div>
            <p v-if="decksSharedWithUser.length < 1" class="no-decks">
              {{ search.length > 0 ? 'No results for this search' : 'No decks have been shared with you yet' }}
            </p>
          </template>
        </div>
      </transition>
    </section>
    <FooterHero />
  </div>
</template>
<script>
import { mapState } from 'vuex'

import QuickStartBanner from '~/components/decks/QuickStartBanner'
import BlankSlateBox from '~/components/decks/BlankSlateBox'
import Navbar from '~/components/Navbar'
import FooterHero from '~/components/Footer'
import DeckList from '~/components/DeckList'
import Announcements from '~/components/Announcements'
import OnboardingDemo from '~/components/OnboardingDemo.vue'

import addSlideDeck from '~/queries/addSlideDeck.gql'
import { getUserDeckList, getTeamDeckList } from '~/queries/getDeckLists.gql'
import { updateSlideDeck, saveDeckEventSettings } from '~/queries/deckQueries.gql'

import { getNameFromPrice } from '~/common/prices'
import DeckTemplateSelector from '~/components/decks/DeckTemplateSelector'
import DeckSettingsModalControls from '~/components/mixins/DeckSettingsModalControls'
import DuplicatesDecks from '~/components/mixins/DuplicatesDecks'
import { defaultThemeData } from '@/common/themeData'
import addNewSlide from '~/queries/addNewSlide'
import { defaultJoinSlide } from '~/common/decks'
import { mergeDeckAndSettingsData } from '~/common/deck-settings'

export default {
  name: 'DeckIndex',
  components: {
    QuickStartBanner,
    BlankSlateBox,
    Announcements,
    Navbar,
    FooterHero,
    DeckList,
    OnboardingDemo,
    TeamContactCta: () => import('~/components/TeamContactCta.vue')
  },
  mixins: [DeckSettingsModalControls, DuplicatesDecks],
  data () {
    return {
      mountedTime: '',
      search: '',
      teamDecks: [],
      publicDecks: [],
      templateDecks: [],
      activeTag: false,
      slideDeckQuery: [],
      changingDeck: false,
      teamDecksLoaded: false,
      firstLoad: true,
      viewLimit: 12,
      sharedByViewLimit: 6,
      sharedWithViewLimit: 6,
      uploadIntent: false,
      tags: ['icebreaker', 'trivia', 'happy hour', 'team building'], // special event, seasonal, gratitude
      sortOrder: 'Last Modified',
      sortOrderOptions: {
        'Last Modified': { dateUpdated: 'desc' },
        'Newest First': { dateCreated: 'desc' },
        'Oldest First': { dateCreated: 'asc' }
      }
    }
  },
  computed: {
    ...mapState([
      'eventPreviews',
      'user'
    ]),
    loading () {
      const isTeamLoading = this.deckView === 'team' && this.hasTeamId ? !this.teamDecks : false
      return this.firstLoad ||
              !this.slideDecks ||
              isTeamLoading ||
              !this.user.email ||
              !this.slideDecksLoaded
    },
    slideDecks () {
      return this.$store.state.slideDecks
    },
    deckView () {
      return this.$route.path === '/decks/team' ? 'team' : 'user'
    },
    hasAnyDecks () {
      return this.slideDecks?.length > 0 || this.teamDecks?.length > 0
    },
    filteredDecks () {
      const decks = this.deckView === 'team' ? this.teamDecks : this.slideDecks

      if (!decks) { return [] }

      const visibleDecks = decks.filter((deck) => {
        if (this.deckView === 'team') {
          const visibleToUsers = deck?.teamData?.visibleToUsers || []
          const visibleToUser = visibleToUsers.map(x => x.toLowerCase()).includes(this.user.email.toLowerCase())
          const isOwner = deck.userId === this.user.id
          return !deck.teamData.isArchived && (deck.teamData.isVisible !== false || visibleToUser || isOwner)
        }
        return !deck.teamData.isArchived
      })

      return visibleDecks.filter((deck) => {
        return deck.name.toLowerCase().includes(this.search.toLowerCase())
      })
    },
    decksSharedByUser () {
      return this.filteredDecks.filter((deck) => {
        return deck.userId === this.user.id
      })
    },
    decksSharedWithUser () {
      return this.filteredDecks.filter((deck) => {
        return deck.userId !== this.user.id
      })
    },
    deckCount () {
      return this.filteredDecks.length
    },
    hasTeamId () {
      return this.user.team?.id
    },
    slideDecksLoaded () {
      return this.$store.state.slideDecksLoaded && (this.teamDecksLoaded || !this.hasTeamId)
    }
  },
  watch: {
    sortOrder () {
      this.viewLimit = 12
      this.sharedByViewLimit = 6
      this.sharedWithViewLimit = 6
    },
    search () {
      this.viewLimit = 12
      this.sharedByViewLimit = 6
      this.sharedWithViewLimit = 6
    },
    deckView () {
      this.viewLimit = 12
      this.sharedByViewLimit = 6
      this.sharedWithViewLimit = 6
    }
  },
  apollo: {
    slideDecks: {
      query: getUserDeckList,
      variables () {
        return {
          userId: this.$auth.user.id,
          orderBy: this.sortOrderOptions[this.sortOrder]
        }
      },
      result ({ data }) {
        // this.log('new result from slideDecks query')
        this.$store.commit('setSlideDecks', data.slideDecks)
        this.$nextTick(() => {
          this.changingDeck = false
          this.firstLoad = false
        })
      },
      pollInterval: 20000,
      fetchPolicy: 'no-cache'
    },
    teamDecks: {
      query: getTeamDeckList,
      variables () {
        return {
          teamId: this.user.team?.id,
          orderBy: this.sortOrderOptions[this.sortOrder]
        }
      },
      skip () {
        return !this.user.team?.id
      },
      result ({ data }) {
        // this.log('new result from teamDecks query')
        this.teamDecksLoaded = true

        this.$nextTick(() => {
          this.changingDeck = false
          this.firstLoad = false
        })
      },
      pollInterval: 20000,
      fetchPolicy: 'no-cache'
    }
  },
  mounted () {
    this.mountedTime = Date.now()

    if (this.$route.query.hasOwnProperty('success')) {
      this.trackPurchase()
    }

    if (this.$route.query.hasOwnProperty('cancel')) {
      this.posthogCapture('upgrade:purchase_cancel', {
        plan_name: getNameFromPrice(Number(this.$route.query.amt)),
        amount: Number(this.$route.query.amt)
      })
    }

    if (this.$route.query.hasOwnProperty('startDeck')) {
      this.startDuplicateDeck()
    }

    if (this.deckView !== 'team') {
      this.teamDecksLoaded = true
    }
  },
  methods: {
    trackPurchase () {
      const event = {
        'transaction_id': '',
        'value': Number(this.$route.query.amt),
        'currency': 'USD',
        items: [
          {
            item_name: getNameFromPrice(Number(this.$route.query.amt))
          }
        ]
      }

      if (this.isDev) {
        console.log('[DEV] Would have tracked event: ', event)
      } else {
        this.$gtag.event('purchase', event)
      }

      this.posthogCapture('upgrade:purchase_success', {
        plan_name: getNameFromPrice(Number(this.$route.query.amt)),
        amount: Number(this.$route.query.amt)
      })

      this.$router.replace({ path: '/decks', query: {} })
    },
    async refreshDeckQueries () {
      await this.$apollo.queries.slideDecks.refresh()
      await this.$apollo.queries.teamDecks.refresh()
    },
    templateSelector () {
      this.posthogCapture('onboard:template_selector_start')
      this.$buefy.modal.open({
        parent: this,
        fullScreen: false,
        component: DeckTemplateSelector,
        trapFocus: true,
        events: {
          // eslint-disable-next-line require-await
          create: async (template) => {
            const templateId = template.id
            const templateName = template.name

            this.posthogCapture('onboard:template_selector_complete', { template_id: templateId, template_name: templateName })

            // Wait for the current modal animation to begin before opening the next modal
            setTimeout(() => {
              this.startDeckSettingsModal(templateId, {
                showBackButton: true,
                trackingData: {
                  domain: 'onboard',
                  templateId,
                  templateName
                }
              }).then(async (data) => {
                if (data && !data.back) {
                  if (templateId) {
                    this.changingDeck = true
                    data.deckName = data.joinSlideTitle || data.deckName
                    await this.duplicateDeck(templateId, data.disabled ? {} : data, { keepName: true })
                    await this.refreshDeckQueries()
                    this.posthogCapture('deck:create_from_template_selector', { from_location: 'template selector', from_deck_id: templateId, from_deck_name: templateName })
                    await this.$router.push('/edit/' + this.highlightDeck)
                  } else {
                    this.posthogCapture('deck:create_blank', { from_location: 'create blank', from_deck_name: 'Blank Deck' })
                    await this.createNewDeck(data)
                  }
                } else if (data && data.back) {
                  setTimeout(() => {
                    this.templateSelector()
                  }, 0)
                }
              })
            }, 0)
          }
        },
        onCancel: () => {
          this.posthogCapture('onboard:template_selector_cancel', { exit_to: 'page' })
        }
      })
    },
    async createNewDeck (deckSettingsData) {
      this.trackEvent(this.uploadIntent ? 'Upload New Deck' : 'New Empty Deck')
      const userId = this.$store.state.auth.user.id

      const response = await this.$apollo.mutate({
        mutation: addSlideDeck,
        variables: { userId }
      })

      const newDeck = response.data.insert_slideDecks_one
      newDeck.themeData = defaultThemeData
      newDeck.name = this.uploadIntent ? 'Untitled Imported Slides' : deckSettingsData.deckName || newDeck.name

      console.log(deckSettingsData)

      const { metaData, themeData, eventSettings } = mergeDeckAndSettingsData(newDeck, deckSettingsData)

      await this.$apollo.mutate({
        mutation: updateSlideDeck,
        variables: {
          id: newDeck.id,
          metaData: metaData || newDeck.metaData,
          themeData: themeData || newDeck.themeData,
          name: newDeck.name,
          isPublic: newDeck.isPublic
        }
      })

      await this.$apollo.mutate({
        mutation: saveDeckEventSettings,
        variables: {
          id: newDeck.id,
          eventSettings
        }
      })

      await this.$apollo.mutate({
        mutation: addNewSlide,
        variables: {
          userId,
          parentDeck: newDeck.id,
          template: 'joinSlide',
          order: 1,
          content: defaultJoinSlide(deckSettingsData.joinSlideTitle).content
        }
      })

      this.$store.commit('setShowImportModal', this.uploadIntent)
      await this.$router.push('/edit/' + newDeck.id)
    },
    async uploadDeck () {
      this.uploadIntent = true
      this.posthogCapture('deck:create_from_import', { from_location: 'import', from_deck_name: 'Imported Deck' })
      await this.createNewDeck({})
    },
    startDuplicateDeck () {
      const deckId = this.$route.query.startDeck
      this.posthogCapture('onboard:register_with_start_deck', { from_location: 'register', from_deck_id: deckId })
      this.$router.replace({ path: '/decks', query: {} })
      this.startDeckSettingsModal(deckId, {
        trackingData: {
          domain: 'onboard',
          templateId: deckId
        }
      }).then(async (data) => {
        if (data) {
          this.changingDeck = true
          await this.duplicateDeck(deckId, data, { keepName: !!data.deckName })
          await this.refreshDeckQueries()

          this.posthogCapture('deck:create_from_register', {
            from_location: 'register', from_deck_id: deckId, from_deck_name: data.deckName
          })

          if (!data.disabled) {
            await this.$router.push('/edit/' + this.highlightDeck)
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.deck-container {
  &.has-decks {
    margin-bottom: 60px;
  }
}
.title.folder {
  font-size: 1.1rem;
  margin-bottom: -10px;
  padding-bottom: 5px;
  font-weight: 500;
  border-bottom: 1px solid rgba(229, 234, 236, 0.9);
}

.no-decks {
  margin: 60px 0 20px;
  color: #4f626d;
  opacity: .7;
  text-align: center;

  &.user {
    opacity: .9;
    border: 1px solid rgba(229, 234, 236, 0.9);
    padding: 70px 0;
    margin: 0 0 20px;
    background-color: rgba(255,255,255,.3);
    border-radius: 3px;
  }
}
</style>
